import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { AlertTriangle, Shield } from 'lucide-react';
import { AdminUser } from '@/services/userManagementService';
import { InfractionSeverity, useInfractionTypes, useSeverityInfo, getStrikePenalty } from '@/services/infractionService';
import { Badge } from '@/components/ui/badge';

interface InfractionModalProps {
  user: AdminUser | null;
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (userId: string, type: string, severity: InfractionSeverity, description: string) => void;
  isLoading: boolean;
}

const InfractionModal: React.FC<InfractionModalProps> = ({
  user,
  isOpen,
  onClose,
  onConfirm,
  isLoading,
}) => {
  const { t } = useTranslation();
  const [infractionType, setInfractionType] = useState('');
  const [severity, setSeverity] = useState<InfractionSeverity>('minor');
  const [description, setDescription] = useState('');

  const INFRACTION_TYPES = useInfractionTypes();
  const SEVERITY_INFO = useSeverityInfo();

  const handleSubmit = () => {
    if (!user || !infractionType || !description.trim()) {
      return;
    }
    onConfirm(user.id, infractionType, severity, description);
    handleClose();
  };

  const handleClose = () => {
    setInfractionType('');
    setSeverity('minor');
    setDescription('');
    onClose();
  };

  if (!user) return null;

  const currentStrikes = user.strike_count || 0;
  const penalty = getStrikePenalty(currentStrikes, severity, t);

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Shield className="h-5 w-5 text-orange-500" />
            {t('adminPanel.infractionModal.title')} - {user.full_name}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6">
          {/* Current Strikes Warning */}
          {currentStrikes > 0 && (
            <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
              <div className="flex items-start gap-2">
                <AlertTriangle className="h-5 w-5 text-orange-600 mt-0.5" />
                <div>
                  <p className="font-semibold text-orange-800">
                    {t('adminPanel.infractionModal.currentStrikes')}: {currentStrikes}/3
                  </p>
                  <p className="text-sm text-orange-700 mt-1">
                    {t('adminPanel.infractionModal.currentStrikesWarning', { count: currentStrikes })}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Infraction Type */}
          <div className="space-y-2">
            <Label htmlFor="infraction-type">{t('adminPanel.infractionModal.infractionType')}</Label>
            <Select value={infractionType} onValueChange={setInfractionType}>
              <SelectTrigger>
                <SelectValue placeholder={t('adminPanel.infractionModal.selectInfractionType')} />
              </SelectTrigger>
              <SelectContent>
                {INFRACTION_TYPES.map((type) => (
                  <SelectItem key={type.value} value={type.value}>
                    {type.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Severity */}
          <div className="space-y-3">
            <Label>{t('adminPanel.infractionModal.severity')}</Label>
            <RadioGroup value={severity} onValueChange={(val) => setSeverity(val as InfractionSeverity)}>
              {Object.entries(SEVERITY_INFO).map(([key, info]) => (
                <div key={key} className="flex items-start space-x-3 border rounded-lg p-3 hover:bg-muted/50">
                  <RadioGroupItem value={key} id={key} className="mt-1" />
                  <div className="flex-1">
                    <label htmlFor={key} className="cursor-pointer">
                      <div className="flex items-center gap-2 mb-1">
                        <Badge className={info.color}>{info.label}</Badge>
                      </div>
                      <p className="text-sm text-muted-foreground">{info.description}</p>
                    </label>
                  </div>
                </div>
              ))}
            </RadioGroup>
          </div>

          {/* Description */}
          <div className="space-y-2">
            <Label htmlFor="description">{t('adminPanel.infractionModal.descriptionLabel')}</Label>
            <Textarea
              id="description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder={t('adminPanel.infractionModal.descriptionPlaceholder')}
              rows={4}
            />
          </div>

          {/* Penalty Preview */}
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <h4 className="font-semibold text-red-900 mb-2">{t('adminPanel.infractionModal.penaltyPreview')}</h4>
            <div className="space-y-1 text-sm">
              <p><span className="font-medium">{t('adminPanel.infractionModal.resultingStrike')}:</span> {penalty.strike}/4</p>
              <p><span className="font-medium">{t('adminPanel.infractionModal.action')}:</span> {penalty.action}</p>
              <p><span className="font-medium">{t('adminPanel.infractionModal.duration')}:</span> {penalty.duration}</p>
            </div>
            {penalty.strike === 4 && (
              <div className="mt-3 flex items-start gap-2 bg-red-100 rounded p-2">
                <AlertTriangle className="h-4 w-4 text-red-700 mt-0.5 flex-shrink-0" />
                <p className="text-xs text-red-800 font-medium">
                  {t('adminPanel.infractionModal.permanentBanWarning')}
                </p>
              </div>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleClose} disabled={isLoading}>
            {t('common.cancel')}
          </Button>
          <Button 
            onClick={handleSubmit} 
            disabled={!infractionType || !description.trim() || isLoading}
            variant={penalty.strike === 4 ? 'destructive' : 'default'}
          >
            {isLoading ? t('adminPanel.infractionModal.registering') : t('adminPanel.infractionModal.registerButton')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default InfractionModal;
